import React from "react";
import { ShieldCheck, FileSearch, Handshake, BadgeCheck, ArrowRight, Phone } from "lucide-react";
import { CONTACT } from "@/data/content";

const POINTS = [
  {
    icon: FileSearch,
    title: "We Find What Adjusters Miss",
    text: "Schuyler documents hail bruising, wind-lifted shingles, and hidden storm damage that untrained inspectors — and insurance adjusters — routinely overlook.",
  },
  {
    icon: Handshake,
    title: "He Speaks Their Language",
    text: "Years of working claims means Schuyler knows exactly how carriers think. He meets the adjuster on-site and advocates hard so your damage actually gets covered.",
  },
  {
    icon: BadgeCheck,
    title: "Paid What You're Owed",
    text: "From the first photo to the final check, Schuyler builds a claim file so thorough that lowball denials get overturned — and you pay little more than your deductible.",
  },
];

const STATS = [
  { value: "98%", label: "Storm Claims Approved" },
  { value: "$0", label: "Beyond Your Deductible*" },
  { value: "24hr", label: "Storm Damage Inspections" },
];

export const InsuranceSpecial = () => {
  return (
    <section id="insurance" data-testid="insurance-section" className="relative bg-[#0A0A0A] overflow-hidden">
      {/* accent glow */}
      <div
        className="absolute -top-32 -right-32 h-96 w-96 rounded-full blur-3xl opacity-25"
        style={{ background: "radial-gradient(circle, #D86C1E 0%, transparent 70%)" }}
      />
      <div className="relative container mx-auto px-6 md:px-12 max-w-7xl py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left headline */}
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 bg-[#D86C1E] text-white px-4 py-2 mb-6">
              <ShieldCheck className="h-4 w-4" />
              <span className="text-xs font-heading font-bold uppercase tracking-[0.2em]">
                Insurance Claims Special
              </span>
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tight leading-[1.05] text-white">
              Storm Damage?
              <br />
              <span className="text-[#D86C1E]">Schuyler Gets It Covered.</span>
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-white/70">
              Most homeowners leave thousands on the table because they don't know how to fight a
              claim. Schuyler does. He's <span className="text-white font-semibold">uniquely skilled
              at getting insurance companies to pay</span> for storm, hail, wind, and monsoon damage —
              turning denials and lowball offers into fully-funded new roofs.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href="#quote"
                data-testid="insurance-cta-button"
                className="group inline-flex items-center justify-center gap-2 bg-[#D86C1E] hover:bg-[#B0551A] text-white font-heading font-bold text-base uppercase tracking-wide px-8 py-4 transition-all hover:-translate-y-1 shadow-lg"
              >
                Free Storm Damage Inspection
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href={`tel:${CONTACT.phoneRaw}`}
                className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-[#D86C1E] text-white font-heading font-bold text-base uppercase tracking-wide px-8 py-4 transition-all"
              >
                <Phone className="h-5 w-5 text-[#D86C1E]" />
                {CONTACT.phone}
              </a>
            </div>

            {/* Stats */}
            <div className="mt-10 grid grid-cols-3 border border-white/10 divide-x divide-white/10">
              {STATS.map((s) => (
                <div key={s.label} className="p-5 text-center">
                  <div className="font-heading text-3xl font-black text-white">{s.value}</div>
                  <div className="text-[10px] uppercase tracking-wider text-white/50 mt-1 leading-tight">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right points */}
          <div className="lg:col-span-7 grid sm:grid-cols-1 gap-px bg-white/10 border border-white/10">
            {POINTS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  data-testid={`insurance-point-${i}`}
                  className="bg-[#0A0A0A] p-8 flex items-start gap-5 group hover:bg-[#141414] transition-colors"
                >
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center bg-[#D86C1E]/15 group-hover:bg-[#D86C1E] transition-colors">
                    <Icon className="h-6 w-6 text-[#D86C1E] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-white mb-2">{p.title}</h3>
                    <p className="text-sm leading-relaxed text-white/60">{p.text}</p>
                  </div>
                </div>
              );
            })}
            <div className="bg-[#0A0A0A] px-8 py-4">
              <p className="text-xs text-white/30">
                *Subject to your individual policy terms. Schuyler will review your coverage with you
                up front — no surprises.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InsuranceSpecial;
